'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Image from 'next/image';

import { PropsWithClassname } from '@/app/types';
import { Routes } from '@/app/enums';

import subprojectIllustration from '@/public/public/images/subproject-illustration.png';

import Button, { ButtonHTMLTag, ButtonSize } from '@/app/components/Button/Button';
import HandshakeIcon from '@/app/components/HandshakeIcon/HandshakeIcon';
import { useIsProjectActive } from '@/app/providers/ProjectProvider';

export interface SubprojectBannerProps extends PropsWithClassname {
  projectNumericId: number;
}

const SubprojectBanner = ({ projectNumericId, className }: SubprojectBannerProps) => {
  const { t } = useTranslation();
  const isProjectActive = useIsProjectActive();

  return (
    <div
      className={classNames(
        className,
        'w-[calc(100% + 48px)] max-w-full lg:max-w-screen-lg lg:w-[744px] p-[24px] lg:p-[40px] lg:ml-0  lg:mr-0 lg:mx-0 flex flex-col lg:flex-row-reverse bg-secondary-light',
      )}
    >
      <div className="w-full lg:w-1/2">
        <Image
          src={subprojectIllustration}
          alt={t('components.SubprojectBanner.imageAlt')}
          className="w-full max-w-[200px] lg:max-w-[290px] mx-auto"
        />
      </div>
      <div className="w-full lg:w-1/2">
        <h3 className="text-[24px] lg:text-[32px] font-medium leading-[32px] lg:leading-[40px] text-balance mb-[16px]">
          {t('components.SubprojectBanner.heading')}
        </h3>
        <p className="text-[16px] leading-[24px] lg:text-[14px]">{t('components.SubprojectBanner.text')}</p>
        <a
          className="block underline text-primary-mid hover:no-underline mb-[16px]"
          href={`${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.CHALLENGE}`}
        >
          {t('components.SubprojectBanner.howItWorks')}
        </a>
        <Button
          size={ButtonSize.LARGE}
          className="flex justify-center lg:inline-block"
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.LINK,
            href: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.NEW_CHALLENGE}/${projectNumericId}`,
            disabled: !isProjectActive,
          }}
        >
          <HandshakeIcon className="inline mr-[12px]" />
          {t('components.SubprojectBanner.cta')}
        </Button>
      </div>
    </div>
  );
};

export default SubprojectBanner;
